import { DashboardCard } from "@dashboard/components/Card";
import Money from "@dashboard/components/Money";
import { DiscountValueTypeEnum } from "@dashboard/graphql";
import { OrderDiscountContext } from "@dashboard/products/components/OrderDiscountProviders/OrderDiscountProvider";
import { Box, Button, Popover, sprinkles, Text } from "@saleor/macaw-ui-next";
import { useContext } from "react";
import { useIntl } from "react-intl";

import OrderDiscountCommonModal from "../OrderDiscountCommonModal";
import { ORDER_DISCOUNT } from "../OrderDiscountCommonModal/types";
import { messages as summaryMessages } from "../OrderDraftDetailsSummary/messages";
import { messages } from "./messages";

// Order-level manual discount (percentage or fixed) for already placed
// orders. Draft/unconfirmed orders edit it in OrderDraftDetailsSummary; the
// backend recalculates a placed order's totals when the discount changes.
const OrderPlacedDiscountCard = () => {
  const intl = useIntl();
  const discountContext = useContext(OrderDiscountContext);

  if (!discountContext) {
    return null;
  }

  const {
    orderDiscount,
    addOrderDiscount,
    removeOrderDiscount,
    openDialog,
    closeDialog,
    isDialogOpen,
    orderDiscountAddStatus,
    orderDiscountRemoveStatus,
    undiscountedPrice,
  } = discountContext;

  return (
    <DashboardCard data-test-id="order-placed-discount">
      <DashboardCard.Header>
        <DashboardCard.Title>{intl.formatMessage(messages.title)}</DashboardCard.Title>
      </DashboardCard.Header>
      <DashboardCard.Content display="flex" flexDirection="column" gap={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={2}>
          {orderDiscount ? (
            <Box display="flex" alignItems="baseline" gap={1}>
              {orderDiscount.calculationMode === DiscountValueTypeEnum.PERCENTAGE && (
                <Text color="default2">{`(${orderDiscount.value}%)`}</Text>
              )}
              <Money money={orderDiscount.amount} />
            </Box>
          ) : (
            <Text color="default2">{intl.formatMessage(messages.noDiscount)}</Text>
          )}
          <Popover
            open={isDialogOpen}
            onOpenChange={open => {
              if (!open) {
                closeDialog();
              }
            }}
          >
            <Popover.Trigger>
              <Button
                variant="secondary"
                data-test-id="placed-order-discount-button"
                onClick={isDialogOpen ? closeDialog : openDialog}
              >
                {intl.formatMessage(
                  orderDiscount ? summaryMessages.discount : summaryMessages.addDiscount,
                )}
              </Button>
            </Popover.Trigger>
            <Popover.Content align="end" className={sprinkles({ zIndex: "3" })}>
              <Box boxShadow="defaultOverlay">
                <OrderDiscountCommonModal
                  modalType={ORDER_DISCOUNT}
                  existingDiscount={orderDiscount}
                  maxPrice={undiscountedPrice}
                  onConfirm={addOrderDiscount}
                  onClose={closeDialog}
                  onRemove={removeOrderDiscount}
                  confirmStatus={orderDiscountAddStatus}
                  removeStatus={orderDiscountRemoveStatus}
                />
              </Box>
            </Popover.Content>
          </Popover>
        </Box>
        <Text size={2} color="default2">
          {intl.formatMessage(messages.hint)}
        </Text>
      </DashboardCard.Content>
    </DashboardCard>
  );
};

OrderPlacedDiscountCard.displayName = "OrderPlacedDiscountCard";
export default OrderPlacedDiscountCard;
